import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generateUniqueId } from '@/lib/idUtils';

export interface NoteItem {
  id: string;
  content: string;
  createdAt: number;
  updatedAt: number;
}

export interface NotesState {
  content: string; // Keep for backwards compatibility
  notes: NoteItem[]; // New numbered list format
  lastUpdated: number | null;
}

const initialState: NotesState = {
  content: '',
  notes: [],
  lastUpdated: null,
};

const notesSlice = createSlice({
  name: 'notes',
  initialState,
  reducers: {
    updateNotes: (state, action: PayloadAction<string>) => {
      state.content = action.payload;
      state.lastUpdated = Date.now();
    },

    addNote: (state, action: PayloadAction<string>) => {
      const newNote: NoteItem = {
        id: generateUniqueId('note'),
        content: action.payload,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      state.notes.push(newNote);
      state.lastUpdated = Date.now();
    },

    setNotes: (state, action: PayloadAction<NoteItem[]>) => {
      state.notes = action.payload;
      state.lastUpdated = Date.now();
    },

    updateNote: (state, action: PayloadAction<{ id: string; content: string }>) => {
      const note = state.notes.find(n => n.id === action.payload.id);
      if (note) {
        note.content = action.payload.content;
        note.updatedAt = Date.now();
        state.lastUpdated = Date.now();
      }
    },

    deleteNote: (state, action: PayloadAction<string>) => {
      state.notes = state.notes.filter(n => n.id !== action.payload);
      state.lastUpdated = Date.now();
    },

    clearNotes: (state) => {
      state.content = '';
      state.notes = [];
      state.lastUpdated = null;
    },
  },
});

export const {
  updateNotes,
  addNote,
  setNotes,
  updateNote,
  deleteNote,
  clearNotes,
} = notesSlice.actions;

export default notesSlice.reducer;
